/**
 * THE FINANCE MENU — which sub-tab lives on which sidebar page.
 *
 * Odoo's Accounting app is a handful of menus, each holding the screens that belong together (Customers, Vendors,
 * Accounting, Reporting, Configuration). Finance here had the opposite shape: one sidebar section, a dozen loose rows,
 * and a 30-tab pill strip on the hub. This file is the one place that says how the 30 tabs group into seven pages.
 *
 * It is DATA ONLY and imports nothing but permission constants, because two readers need it and neither may pull a
 * screen in with it: the sidebar registry (to build the page rows and the search entries) and the shell (to draw the
 * sub-tab bar). `FinanceHubPage` still declares the tabs themselves; `finance-menu.test.ts` fails the moment the two
 * disagree about an id, a label or a permission.
 *
 * Property names are `tab` / `title` / `needs` on purpose. The access-manifest extractor treats any object with
 * `id` + `label` as a hub tab, and this is a menu, not a second list of tabs.
 */
import {
  BankManagementPermissions,
  CashierPermissions,
  CashManagementPermissions,
  FinanceAccountingPermissions,
  FinancialPermissions,
  MasterDataPermissions,
  PayablePermissions,
  ReceivablePermissions,
} from '@nexao/contracts';

export interface FinanceMenuTab {
  /** The hub tab id — the `?tab=` value. */
  readonly tab: string;
  readonly title: string;
  /** The ONE permission the hub gives this tab. Exactly one, so "any tab of the page" is a flat list. */
  readonly needs: string;
}

export interface FinanceMenuPage {
  readonly page: string;
  readonly title: string;
  /** The tab the sidebar row opens. A user who cannot open it lands on the first tab of this page they can. */
  readonly landing: string;
  readonly tabs: readonly FinanceMenuTab[];
}

/**
 * `/finance` itself is guarded by this permission in `AppRouter`. Every row or search entry that opens the hub needs it
 * too — a row shown to someone the route then turns away is a menu advertising a 403. `finance-menu.test.ts` fails if
 * the route's guard ever stops being this.
 */
export const FINANCE_ROUTE_PERMISSION = FinanceAccountingPermissions.FINANCE_LEDGER_READ;

const LEDGER = FinanceAccountingPermissions.FINANCE_LEDGER_READ;
const TRIAL = FinanceAccountingPermissions.FINANCE_TRIAL_BALANCE_READ;
const AR = ReceivablePermissions.RECEIVABLE_LEDGER_READ;
const AP = PayablePermissions.PAYABLE_LEDGER_READ;

/**
 * Seven pages, in the order the sidebar lists them: money owed to us, money we owe, the books, the cash and bank
 * registers, the banking movements, the statements, and the masters behind them.
 */
export const FINANCE_PAGES: readonly FinanceMenuPage[] = [
  {
    page: 'receivables',
    title: 'Receivables',
    landing: 'ar-ledger',
    tabs: [
      { tab: 'invoices', title: 'Invoices', needs: AR },
      { tab: 'ar-ledger', title: 'AR Ledger', needs: AR },
      { tab: 'outstanding-ageing', title: 'Outstanding & Ageing', needs: AR },
    ],
  },
  {
    page: 'payables',
    title: 'Payables',
    landing: 'vendor-ledger',
    tabs: [
      { tab: 'vendors', title: 'Vendors', needs: MasterDataPermissions.VENDOR_MASTER_READ },
      { tab: 'vendor-invoices', title: 'Vendor Invoices', needs: AP },
      { tab: 'vendor-ledger', title: 'Vendor Ledger', needs: AP },
      { tab: 'outstanding-payables-ageing', title: 'Outstanding Payables & Ageing', needs: AP },
      { tab: 'payment-advice', title: 'Payment Advice', needs: AP },
    ],
  },
  {
    page: 'accounting',
    title: 'Accounting',
    landing: 'journal-vouchers',
    tabs: [
      { tab: 'voucher-entry', title: 'Voucher Entry', needs: FinanceAccountingPermissions.FINANCE_JOURNAL_CREATE },
      { tab: 'journal-vouchers', title: 'Journal Vouchers', needs: LEDGER },
      { tab: 'chart-of-accounts', title: 'Chart of Accounts', needs: LEDGER },
    ],
  },
  {
    page: 'cash-bank',
    title: 'Cash & Bank',
    landing: 'cash-book',
    tabs: [
      { tab: 'cash-book', title: 'Cash Book', needs: CashManagementPermissions.CASH_BOOK_READ },
      { tab: 'bank-book', title: 'Bank Book', needs: BankManagementPermissions.BANK_BOOK_READ },
      { tab: 'cash-summary', title: 'Cash Summary', needs: CashManagementPermissions.CASH_SUMMARY_READ },
      { tab: 'bank-summary', title: 'Bank Summary', needs: BankManagementPermissions.BANK_SUMMARY_READ },
      { tab: 'cash-sessions', title: 'Cash Sessions', needs: CashierPermissions.CASHIER_SESSION_OPEN },
    ],
  },
  {
    page: 'banking',
    title: 'Banking',
    landing: 'bank-deposits',
    tabs: [
      { tab: 'bank-deposits', title: 'Bank Deposits', needs: BankManagementPermissions.BANK_DEPOSIT_CREATE },
      { tab: 'bank-withdrawals', title: 'Bank Withdrawals', needs: BankManagementPermissions.BANK_WITHDRAWAL_CREATE },
      { tab: 'bank-transfers', title: 'Bank Transfers', needs: BankManagementPermissions.BANK_TRANSFER_CREATE },
      { tab: 'cash-transfers', title: 'Cash Transfers', needs: CashManagementPermissions.CASH_TRANSFER_CREATE },
      { tab: 'cheque-register', title: 'Cheque Register', needs: BankManagementPermissions.CHEQUE_MANAGE },
    ],
  },
  {
    page: 'reporting',
    title: 'Reporting',
    landing: 'trial-balance',
    tabs: [
      { tab: 'trial-balance', title: 'Trial Balance', needs: TRIAL },
      { tab: 'general-ledger', title: 'General Ledger', needs: LEDGER },
      { tab: 'profit-loss', title: 'Profit & Loss', needs: TRIAL },
      { tab: 'balance-sheet', title: 'Balance Sheet', needs: TRIAL },
      { tab: 'day-book', title: 'Day Book', needs: LEDGER },
      { tab: 'gst-statement', title: 'GST Statement', needs: TRIAL },
    ],
  },
  {
    page: 'setup',
    title: 'Setup',
    landing: 'master-setup',
    tabs: [
      { tab: 'master-setup', title: 'Master Setup', needs: FinancialPermissions.FINANCIAL_ACCOUNT_READ },
      { tab: 'financial-year', title: 'Financial Year', needs: LEDGER },
      { tab: 'bank-accounts', title: 'Bank Accounts', needs: BankManagementPermissions.BANK_ACCOUNT_MANAGE },
      { tab: 'cash-counters', title: 'Cash Counters', needs: CashManagementPermissions.CASH_COUNTER_MANAGE },
    ],
  },
];

/** Registry id of a page's sidebar row. */
export const financePageRowId = (page: string): string => `fin-page-${page}`;
/** Registry id of a tab's search/breadcrumb entry. */
export const financeTabEntryId = (tab: string): string => `fin-tab-${tab}`;
export const financeTabUrl = (tab: string): string => `/finance?tab=${tab}`;

export function financePageOfTab(tab: string): FinanceMenuPage | undefined {
  return FINANCE_PAGES.find((p) => p.tabs.some((t) => t.tab === tab));
}

/** Every permission that opens at least one tab of the page — the sidebar row shows if the user holds ANY of them. */
export function financePageNeeds(page: FinanceMenuPage): string[] {
  return [...new Set(page.tabs.map((t) => t.needs))];
}

/** The tab to show when `requested` is not openable by this user: the first tab of ITS page they can open. */
export function firstOpenableTabOfPage(page: FinanceMenuPage, openable: ReadonlySet<string>): string | undefined {
  return page.tabs.find((t) => openable.has(t.tab))?.tab;
}
